import { useMemo } from 'react';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { vars } from 'src/core/themes';
import { useUserHasTagTypePermissions } from 'src/hooks/useUserHasTagTypePermissions';
import { IconsFill, PlusIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Tag } from 'src/WebApiController';

import { ButtonWithIcon } from '../Buttons/ButtonWithIcon';
import { CreateTagMetadataDialog } from './TagMetadataForm/CreateTagMetadataDialog';
import * as styles from './TagsFormBody.css';
import { TagsFormBodyProps } from './TagsFormBody.types';
import { TagsPanel } from './TagsFormBodyV3/TagsPanel';
import { useTagsFormBody } from './useTagsFormBody';

export const TagsFormBodyV3 = ({
  disabled,
  tagsCurrentEntity,
  onTagsUpdate,
  entityType,
  canEditTags,
}: TagsFormBodyProps) => {
  const { hasCreateTagTypePermission } =
    useUserHasTagTypePermissions(entityType);

  const { tagsWithValue, onTagsUpdateHandler, existingTagsCurrentType } =
    useTagsFormBody(entityType, tagsCurrentEntity, onTagsUpdate);

  const {
    openDialog: openCreateTagMetadataDialog,
    closeDialog: closeCreateTagMetadataDialog,
  } = useDialog(DialogId.CreateTagMetadata, CreateTagMetadataDialog);

  const tagMetadatasWithSourceEntityId = useMemo(() => {
    const result: Tag[] = [];
    (existingTagsCurrentType ?? []).forEach((tagMetadata) => {
      const sourceEntityIds = Array.from(
        new Set(
          tagsCurrentEntity
            .filter((t) => t.key === tagMetadata.key)
            .map((t) => t.sourceEntityId)
        )
      );
      if (tagMetadata.persistentTagId != null && sourceEntityIds.length) {
        sourceEntityIds.forEach((seid) => {
          result.push({
            ...tagMetadata,
            sourceEntityId: seid,
          });
        });
      } else {
        result.push(tagMetadata);
      }
    });

    return result.toSorted((a, b) => {
      const lowestOrdinalA =
        tagsCurrentEntity
          .filter((t) => t.key === a.key)
          .toSorted((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))[0]
          ?.ordinal ?? Number.MAX_SAFE_INTEGER;
      const lowestOrdinalB =
        tagsCurrentEntity
          .filter((t) => t.key === b.key)
          .toSorted((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))[0]
          ?.ordinal ?? Number.MAX_SAFE_INTEGER;

      return lowestOrdinalA - lowestOrdinalB;
    });
  }, [existingTagsCurrentType, tagsCurrentEntity]);

  return (
    <>
      <div className={styles.mainContent}>
        <TagsPanel
          tagMetadatas={tagMetadatasWithSourceEntityId}
          tagsWithValue={tagsWithValue}
          onTagsUpdate={onTagsUpdateHandler}
          disabled={disabled || !canEditTags}
          entityType={entityType}
        />
        <div className={styles.editIconContainer}>
          <ButtonWithIcon
            icon={
              <PlusIcon
                withHoverEffect
                fill={IconsFill.textBrand}
                size={vars.iconSize.m}
              />
            }
            disabled={disabled || !hasCreateTagTypePermission}
            textContentId={ContentId.CreateNewTag}
            onClick={() => {
              openCreateTagMetadataDialog({
                onClose: closeCreateTagMetadataDialog,
                entityType,
              });
            }}
            style={{ width: 'min-content', whiteSpace: 'nowrap' }}
            variant={'text'}
          />
        </div>
      </div>
    </>
  );
};
